import React, { useState } from 'react';
import { Heading, InlineTextButton } from '../../components';
import { richText } from '../../util/richText';
import { FormattedMessage } from '../../util/reactIntl';
import classNames from 'classnames';
import { ReactComponent as InfoIcon } from '../../assets/icons/information-circle/outline.svg';

import css from './ListingPage.module.css';

const MIN_LENGTH_FOR_LONG_WORDS = 20;
// Character threshold for showing expand/collapse button
// CSS line-clamp enforces exactly 3 visible lines regardless
const DESCRIPTION_COLLAPSED_LENGTH = 170;

const DescriptionText = props => {
  const { className, content, isExpanded } = props;

  const textClasses = classNames(className, {
    [css.descriptionTextCollapsed]: !isExpanded,
    [css.descriptionTextExpanded]: isExpanded,
  });

  return <p className={textClasses}>{content}</p>;
};

const ExpandableDescription = props => {
  const [expand, setExpand] = useState(false);
  const { text, textClass } = props;

  const handleToggleClick = () => {
    setExpand(!expand);
  };

  const content = richText(text, {
    linkify: true,
    longWordMinLength: MIN_LENGTH_FOR_LONG_WORDS,
    longWordClass: css.longWord,
    breakChars: '/',
  });

  // Check if description is long enough to need expansion
  const needsExpansion = text.length >= DESCRIPTION_COLLAPSED_LENGTH;

  return (
    <>
      <DescriptionText className={textClass} content={content} isExpanded={expand} />
      {needsExpansion && (
        <InlineTextButton
          rootClassName={expand ? css.showLessDescription : css.showMoreDescription}
          onClick={handleToggleClick}
        >
          <FormattedMessage
            id={expand ? 'ListingPage.showLessDescription' : 'ListingPage.showMoreDescription'}
          />
        </InlineTextButton>
      )}
    </>
  );
};

const SectionTextMaybe = props => {
  let { text, heading, showAsIngress = false, isDescription = false } = props;
  const [showConditionTooltip, setShowConditionTooltip] = useState(false);

  if (isDescription) {
    heading = 'Beschreibung';
  }
  const textClass = isDescription ? css.descriptionText : showAsIngress ? css.ingress : css.text;
  const sectionClass = isDescription ? css.descriptionSectionText : css.sectionText;
  const headingClass = isDescription ? css.descriptionHeading : css.sectionHeading;

  // Check if this is the condition (Zustand) field
  const isConditionField = heading === 'Zustand';

  // Use expandable description for description section
  const textContent = isDescription ? (
    <ExpandableDescription text={text} textClass={textClass} />
  ) : (
    <p className={textClass}>
      {richText(text, {
        linkify: true,
        longWordMinLength: MIN_LENGTH_FOR_LONG_WORDS,
        longWordClass: css.longWord,
        breakChars: '/',
      })}
    </p>
  );

  return text ? (
    <section className={sectionClass}>
      {heading ? (
        <div className={css.headingWithInfo}>
          <Heading as="h2" rootClassName={headingClass}>
            {heading}
          </Heading>
          {isConditionField && (
            <>
              <button
                type="button"
                className={css.conditionInfoButton}
                onClick={() => setShowConditionTooltip(v => !v)}
                aria-label="Info about condition"
              >
                <InfoIcon className={css.conditionInfoIcon} />
              </button>
              {showConditionTooltip && (
                <div className={css.conditionTooltip} role="tooltip">
                  <strong>Zustand</strong>
                  <p className={css.conditionTooltipIntro}>
                    Der Artikelstatus hilft Mitgliedern, den Katalog zu filtern und Artikel
                    leichter zu finden.
                  </p>
                  <ul className={css.conditionTooltipList}>
                    <li>
                      <strong>Neu:</strong> Das Trikot ist komplett ungetragen, originalverpackt
                      und ohne Gebrauchsspuren.
                    </li>
                    <li>
                      <strong>Sehr gut:</strong> Das Trikot wurde nur selten getragen, hat
                      minimale Gebrauchsspuren und sieht fast aus wie neu aus.
                    </li>
                    <li>
                      <strong>Gut:</strong> Das Trikot wurde regelmäßig benutzt, weist sichtbare,
                      aber keine gravierenden Gebrauchsspuren auf.
                    </li>
                    <li>
                      <strong>Akzeptabel:</strong> Sichtbare Gebrauchsspuren wie leichtes
                      Ausbleichen oder winzige Löcher, jedoch weiterhin tragbar und sammelwürdig.
                    </li>
                    <li>
                      <strong>Schlecht:</strong> Stark getragen, mit deutlichen
                      Abnutzungsmerkmalen wie Flecken, Rissen oder beschädigter Beflockung.
                    </li>
                  </ul>
                </div>
              )}
            </>
          )}
        </div>
      ) : null}
      {textContent}
    </section>
  ) : null;
};

export default SectionTextMaybe;
